import { DefaultButton } from "../../../../common/components/button/defaultButton/DefaultButton";
import { IconButton } from "../../../../common/components/button/iconButton/IconButton";
import { IconType } from "../../../../common/icons/Icon";
import { MessageBanner } from "../../../../common/components/common/MessageBanner/MessageBanner";
import { getSuccessToast } from "../../../../common/infrastructure/utilities/toastUtility";
import { useMcpSection } from "../hooks/useMcpSection";
import { IMcpToken } from "../models/mcpModels";
import styles from "./McpSection.module.scss";

const formatDate = (iso: string | null): string => {
  if (!iso) {
    return "";
  }
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString("bg-BG");
};

export const McpSection = () => {
  const {
    serverUrl,
    tokens,
    isFetching,
    isGenerating,
    isRevoking,
    name,
    setName,
    newToken,
    dismissNewToken,
    handleGenerate,
    handleRevoke,
  } = useMcpSection();

  const copy = (value: string) => {
    navigator.clipboard?.writeText(value);
    getSuccessToast("Копирано.");
  };

  // Only skip TLS verification for a local, self-signed dev server — never for a real host.
  const isLocalhost = /localhost|127\.0\.0\.1/.test(serverUrl);

  const buildDesktopConfig = (token: string) =>
    JSON.stringify(
      {
        mcpServers: {
          planny: {
            command: "npx",
            args: [
              "-y",
              "mcp-remote",
              serverUrl,
              "--header",
              `Authorization: Bearer ${token}`,
            ],
            ...(isLocalhost
              ? { env: { NODE_TLS_REJECT_UNAUTHORIZED: "0" } }
              : {}),
          },
        },
      },
      null,
      2
    );

  const buildClaudeCodeCommand = (token: string) =>
    isLocalhost
      ? `claude mcp add planny -e NODE_TLS_REJECT_UNAUTHORIZED=0 -- npx -y mcp-remote ${serverUrl} --header "Authorization: Bearer ${token}"`
      : `claude mcp add --transport http planny ${serverUrl} --header "Authorization: Bearer ${token}"`;

  return (
    <div className={styles.root}>
      <MessageBanner
        type="info"
        width="100%"
        message="Свържете Claude или ChatGPT към вашите данни в Planny. Достъпът е само за четене — AI клиентът може да преглежда резервации, статистики и настройки, но не може да променя нищо."
      />

      <div className={styles.card}>
        <span className={styles.cardTitle}>Адрес на MCP сървъра</span>
        <div className={styles.copyRow}>
          <code className={styles.code}>{serverUrl}</code>
          <DefaultButton
            label="Копирай"
            design="neutral"
            onClick={() => copy(serverUrl)}
            width="auto"
          />
        </div>
      </div>

      <div className={styles.card}>
        <span className={styles.cardTitle}>Нов токен за достъп</span>
        <p className={styles.hint}>
          Дайте име, за да разпознавате токена по-късно (напр. „Claude — лаптоп“).
        </p>
        <div className={styles.generateRow}>
          <input
            className={styles.input}
            type="text"
            value={name}
            maxLength={100}
            placeholder="Име на токена (по избор)"
            onChange={(e) => setName(e.target.value)}
          />
          <DefaultButton
            label={isGenerating ? "Генерира..." : "Генерирай токен"}
            design="accent"
            onClick={handleGenerate}
            disabled={isGenerating}
            width="auto"
          />
        </div>
      </div>

      {newToken && (
        <div className={`${styles.card} ${styles.tokenReveal}`}>
          <span className={styles.cardTitle}>Копирайте токена сега</span>
          <MessageBanner
            type="warning"
            width="100%"
            message="Това е единственият път, в който ще видите пълния токен. Съхранете го на сигурно място."
          />
          <div className={styles.copyRow}>
            <code className={styles.code}>{newToken.token}</code>
            <DefaultButton
              label="Копирай"
              design="accent"
              onClick={() => copy(newToken.token)}
              width="auto"
            />
          </div>

          <div className={styles.quickConnect}>
            <span className={styles.quickConnectLabel}>
              Бърза връзка (готова конфигурация с този токен):
            </span>
            <DefaultButton
              label="Копирай конфигурация за Claude Desktop"
              design="neutral"
              width="100%"
              onClick={() => copy(buildDesktopConfig(newToken.token))}
            />
            <DefaultButton
              label="Копирай команда за Claude Code"
              design="neutral"
              width="100%"
              onClick={() => copy(buildClaudeCodeCommand(newToken.token))}
            />
            <DefaultButton
              label="Копирай URL с токен (за диалога „Add connector“)"
              design="neutral"
              width="100%"
              onClick={() => copy(`${serverUrl}?token=${newToken.token}`)}
            />
          </div>

          <DefaultButton
            label="Готово"
            design="neutral"
            onClick={dismissNewToken}
            width="100%"
          />
        </div>
      )}

      <div className={styles.card}>
        <span className={styles.cardTitle}>Активни токени</span>
        {isFetching && tokens.length === 0 ? (
          <p className={styles.hint}>Зарежда...</p>
        ) : tokens.length === 0 ? (
          <p className={styles.hint}>Все още няма създадени токени.</p>
        ) : (
          <ul className={styles.tokenList}>
            {tokens.map((token: IMcpToken) => (
              <li key={token.id} className={styles.tokenItem}>
                <div className={styles.tokenInfo}>
                  <span className={styles.tokenName}>{token.name}</span>
                  <span className={styles.tokenPrefix}>{token.tokenPrefix}…</span>
                  <span className={styles.tokenMeta}>
                    Създаден {formatDate(token.createdOn)}
                    {token.lastUsedOn
                      ? ` · последно ползван ${formatDate(token.lastUsedOn)}`
                      : " · не е ползван"}
                  </span>
                </div>
                <IconButton
                  iconType={IconType.Cancel}
                  iconColor="#ff5c5c"
                  iconWidth="18px"
                  iconHeight="18px"
                  tooltip="Премахни"
                  disabled={isRevoking}
                  className={styles.revokeButton}
                  onClick={() => handleRevoke(token)}
                />
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className={styles.card}>
        <span className={styles.cardTitle}>Как да свържете</span>
        <div className={styles.instructions}>
          <p className={styles.note}>
            Попитайте вашия AI асистент как да добавите MCP сървър с този адрес —
            той ще ви поведе по стъпките. Ще трябва да му предоставите URL-ът на
            MCP сървъра и вашия персонален токен (имате ги по-горе).
          </p>
          <p className={styles.note}>
            Ако се затрудните, пишете на поддръжката ни и ще Ви помогнем!
          </p>
        </div>
      </div>
    </div>
  );
};
